import { Box, Text, VStack } from "@chakra-ui/react";
import { TbArrowFork } from "react-icons/tb";
import { FaRegEnvelope } from "react-icons/fa";
import {
  Background,
  Controls,
  Edge,
  MarkerType,
  Node,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  KYDEngageAutomationDefinition,
  KYDEngageAutomationStep,
} from "@common/interfaces/KYDEngageAutomation";
import { useEffect, useMemo, useState, type CSSProperties } from "react";

const nodeHeight = 72;
const nodeWidth = 300;
const entryNodeWidth = nodeWidth / 2;
const laneXGap = 350;
const levelYGap = 170;
const baseX = 260;
const baseY = 180;
const fitViewOptions = {
  padding: 0.25,
  minZoom: 0.2,
  maxZoom: 1,
};

const ENTRY_NODE_ID = "__entry__";
const EXIT_NODE_PREFIX = "__exit__";

type EdgeKind = "next" | "true" | "false";
type GraphNodeKind = "STEP" | "EXIT";

interface GraphNodeDef {
  id: string;
  kind: GraphNodeKind;
  step?: KYDEngageAutomationStep;
}

interface GraphEdgeDef {
  sourceId: string;
  targetId: string;
  kind: EdgeKind;
}

const pushToMapArray = <K, T>(map: Map<K, T[]>, key: K, value: T) => {
  if (!map.has(key)) {
    map.set(key, []);
  }
  map.get(key)!.push(value);
};

const buildOutgoingEdgesMap = (edges: GraphEdgeDef[]) => {
  const outgoing = new Map<string, GraphEdgeDef[]>();
  for (const edge of edges) {
    pushToMapArray(outgoing, edge.sourceId, edge);
  }
  return outgoing;
};

const getEdgeVisual = (kind: EdgeKind) => {
  if (kind === "true") {
    return {
      animated: true,
      label: "true",
      color: "#16A34A",
      style: { stroke: "#16A34A", strokeWidth: 2, strokeDasharray: "6 4" },
      labelStyle: { fill: "#166534", fontSize: 12, fontWeight: 700 },
      labelBgStyle: { fill: "#FFFFFF", fillOpacity: 0.9 },
      labelBgPadding: [2, 4] as [number, number],
    };
  }
  if (kind === "false") {
    return {
      animated: true,
      label: "false",
      color: "#DC2626",
      style: { stroke: "#DC2626", strokeWidth: 2, strokeDasharray: "6 4" },
      labelStyle: { fill: "#991B1B", fontSize: 12, fontWeight: 700 },
      labelBgStyle: { fill: "#FFFFFF", fillOpacity: 0.9 },
      labelBgPadding: [2, 4] as [number, number],
    };
  }

  return {
    animated: false,
    label: undefined,
    color: "#667085",
    style: { stroke: "#667085", strokeWidth: 1.8 },
    labelStyle: undefined,
    labelBgStyle: undefined,
    labelBgPadding: undefined,
  };
};

const AUTOMATION_DISPLAY = {
  triggerEvent: {
    "checkout.completed": "Customer completes checkout",
    "first.checkout.completed": "Customer completes first checkout",
    "event.cart.expired": "Customer's cart expired",
    "event.ticket.checkedin": "Customer checked in a ticket to an event",
  },
  choiceFacts: {
    has_attended_genres: "Has attended genres",
    has_bought_tickets_last_x_days: "Has bought tickets last X days",
    is_first_purchase: "Is first purchase",
    is_cart_abandonment_eligible: "Is cart abandonment eligible",
  },
  triggerSchedule: {
    DAILY: "Daily",
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
  },
};

const getEntryLabel = (automation: KYDEngageAutomationDefinition) => {
  const trigger = automation.trigger;
  if (trigger.type === "EVENT") {
    if (trigger.event_detail?.first_time_for_org) {
      return `${
        AUTOMATION_DISPLAY.triggerEvent[`first.${trigger.event_type}`]
      }`;
    }
    return `${AUTOMATION_DISPLAY.triggerEvent[trigger.event_type]}`;
  }
  return AUTOMATION_DISPLAY.triggerSchedule[trigger.cadence];
};

const delayedMinutesLabel = (minutes: number) => {
  const days = Math.floor(minutes / 1440);
  const remainingHours = Math.floor((minutes % 1440) / 60);
  const remainingMinutes = minutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (remainingHours > 0) {
    parts.push(`${remainingHours}h`);
  }
  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes}m`);
  }

  return parts.join(" ");
};

const buildChoiceTargetsMap = (steps: KYDEngageAutomationStep[]) => {
  const targetToParents = new Map<string, Set<string>>();
  for (const step of steps) {
    if (step.type !== "CHOICE") {
      continue;
    }
    for (const targetId of [step.next_step_id_true, step.next_step_id_false]) {
      if (!targetToParents.has(targetId)) {
        targetToParents.set(targetId, new Set());
      }
      targetToParents.get(targetId)!.add(step.id);
    }
  }
  return targetToParents;
};

const getCalculatedNextStepId = (
  automation: KYDEngageAutomationDefinition,
  stepId: string,
  targetToParents: Map<string, Set<string>>
) => {
  const index = automation.steps.findIndex((step) => step.id === stepId);
  if (index < 0 || index >= automation.steps.length - 1) {
    return undefined;
  }

  const nextStep = automation.steps[index + 1];
  if (!nextStep) {
    return undefined;
  }

  const currentParents = targetToParents.get(stepId);
  const nextParents = targetToParents.get(nextStep.id);
  if (currentParents && nextParents) {
    const sharesChoiceParent = Array.from(currentParents).some((parentId) =>
      nextParents.has(parentId)
    );
    if (sharesChoiceParent) {
      return undefined;
    }
  }

  return nextStep.id;
};

const getExitNodeId = (sourceStepId: string, branch: "true" | "false") =>
  `${EXIT_NODE_PREFIX}${sourceStepId}__${branch}`;

const buildGraph = (automation: KYDEngageAutomationDefinition) => {
  const stepMap = new Map(automation.steps.map((step) => [step.id, step]));
  const targetToParents = buildChoiceTargetsMap(automation.steps);

  const nodes = new Map<string, GraphNodeDef>();
  const edges: GraphEdgeDef[] = [];

  for (const step of automation.steps) {
    nodes.set(step.id, { id: step.id, kind: "STEP", step });
  }

  const addBranchEdge = (
    sourceId: string,
    branch: "true" | "false",
    rawTargetId: string
  ) => {
    if (rawTargetId === "exit") {
      const exitId = getExitNodeId(sourceId, branch);
      if (!nodes.has(exitId)) {
        nodes.set(exitId, {
          id: exitId,
          kind: "EXIT",
        });
      }
      edges.push({ sourceId, targetId: exitId, kind: branch });
      return;
    }

    if (!stepMap.has(rawTargetId)) {
      return;
    }

    edges.push({ sourceId, targetId: rawTargetId, kind: branch });
  };

  for (const step of automation.steps) {
    if (step.type === "CHOICE") {
      addBranchEdge(step.id, "true", step.next_step_id_true);
      addBranchEdge(step.id, "false", step.next_step_id_false);
      continue;
    }

    const nextStepId = getCalculatedNextStepId(
      automation,
      step.id,
      targetToParents
    );
    if (!nextStepId || !stepMap.has(nextStepId)) {
      continue;
    }

    edges.push({ sourceId: step.id, targetId: nextStepId, kind: "next" });
  }

  return { nodes, edges };
};

const buildDepths = (
  startId: string,
  nodes: Map<string, GraphNodeDef>,
  edges: GraphEdgeDef[]
) => {
  const depths = new Map<string, number>();
  if (!nodes.has(startId)) {
    return depths;
  }

  const outgoing = buildOutgoingEdgesMap(edges);

  depths.set(startId, 0);
  const queue = [startId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const depth = depths.get(currentId) || 0;

    for (const edge of outgoing.get(currentId) || []) {
      if (!depths.has(edge.targetId)) {
        depths.set(edge.targetId, depth + 1);
        queue.push(edge.targetId);
      }
    }
  }

  for (const nodeId of Array.from(nodes.keys())) {
    if (!depths.has(nodeId)) {
      depths.set(nodeId, 0);
    }
  }

  return depths;
};

const buildLanes = (
  startId: string,
  nodes: Map<string, GraphNodeDef>,
  edges: GraphEdgeDef[],
  depths: Map<string, number>
) => {
  const lanes = new Map<string, number>();
  if (!nodes.has(startId)) {
    return lanes;
  }

  const byDepth = new Map<number, string[]>();
  for (const [nodeId, depth] of Array.from(depths.entries())) {
    if (!byDepth.has(depth)) {
      byDepth.set(depth, []);
    }
    byDepth.get(depth)!.push(nodeId);
  }

  const outgoing = buildOutgoingEdgesMap(edges);

  lanes.set(startId, 0);
  const desiredByTarget = new Map<string, number[]>();

  const maxDepth = Math.max(...Array.from(depths.values()));

  for (let depth = 0; depth <= maxDepth; depth += 1) {
    const ids = byDepth.get(depth) || [];

    ids.sort((a, b) => {
      const laneA = lanes.get(a);
      const laneB = lanes.get(b);
      if (laneA !== undefined && laneB !== undefined) {
        return laneA - laneB;
      }
      if (laneA !== undefined) {
        return -1;
      }
      if (laneB !== undefined) {
        return 1;
      }
      return a.localeCompare(b);
    });

    for (const nodeId of ids) {
      if (!lanes.has(nodeId)) {
        const desired = desiredByTarget.get(nodeId) || [0];
        const avg = Math.round(
          desired.reduce((s, v) => s + v, 0) / desired.length
        );
        lanes.set(nodeId, avg);
      }

      const lane = lanes.get(nodeId) || 0;
      for (const edge of outgoing.get(nodeId) || []) {
        const targetDepth = depths.get(edge.targetId) || 0;
        if (targetDepth <= depth) {
          continue;
        }

        const offset =
          edge.kind === "true" ? 1 : edge.kind === "false" ? -1 : 0;
        pushToMapArray(desiredByTarget, edge.targetId, lane + offset);
      }
    }

    const nextDepthIds = byDepth.get(depth + 1) || [];
    const occupied = new Set<number>();

    for (const nodeId of nextDepthIds) {
      const desired = desiredByTarget.get(nodeId) || [0];
      let lane = Math.round(
        desired.reduce((s, v) => s + v, 0) / desired.length
      );
      while (occupied.has(lane)) {
        lane += 1;
      }
      occupied.add(lane);
      lanes.set(nodeId, lane);
    }
  }

  const minLane = Math.min(...Array.from(lanes.values()));
  if (minLane < 0) {
    for (const [id, lane] of Array.from(lanes.entries())) {
      lanes.set(id, lane - minLane);
    }
  }

  return lanes;
};

const buildNodesAndEdges = (
  automation: KYDEngageAutomationDefinition,
  selectedStepId?: string
): { nodes: Node[]; edges: Edge[] } => {
  if (automation.steps.length === 0) {
    return { nodes: [], edges: [] };
  }

  const { nodes: graphNodes, edges: graphEdges } = buildGraph(automation);
  const startId = automation.steps[0].id;

  const depths = buildDepths(startId, graphNodes, graphEdges);
  const lanes = buildLanes(startId, graphNodes, graphEdges, depths);

  const positions = new Map<string, { x: number; y: number }>();
  for (const [nodeId, node] of Array.from(graphNodes.entries())) {
    const lane = lanes.get(nodeId) || 0;
    const depth = depths.get(nodeId) || 0;

    const x = baseX + lane * laneXGap;
    const y = baseY + depth * levelYGap;

    if (node.kind === "EXIT") {
      positions.set(nodeId, {
        x: x + (nodeWidth - 160) / 2,
        y,
      });
      continue;
    }

    positions.set(nodeId, { x, y });
  }

  const nodes: Node[] = [];

  const startPos = positions.get(startId) || { x: baseX, y: baseY };
  nodes.push({
    id: ENTRY_NODE_ID,
    data: {
      label:
        getEntryLabel(automation) ||
        "Customers that match the automation conditions",
    },
    position: {
      x: startPos.x + (nodeWidth - entryNodeWidth) / 2,
      y: startPos.y - levelYGap,
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    selectable: false,
    draggable: false,
    style: {
      width: entryNodeWidth,
      minHeight: nodeHeight,
      borderRadius: 20,
      border: "1px dashed #94A3B8",
      fontSize: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      fontWeight: 600,
      background: "#FFFFFF",
    } as CSSProperties,
  });

  for (const [nodeId, node] of Array.from(graphNodes.entries())) {
    const pos = positions.get(nodeId);
    if (!pos) {
      continue;
    }

    if (node.kind === "EXIT") {
      nodes.push({
        id: nodeId,
        data: { label: "Exit" },
        position: pos,
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        selectable: false,
        draggable: false,
        style: {
          width: 160,
          minHeight: 56,
          borderRadius: 999,
          border: "1px dashed #94A3B8",
          background: "#F8FAFC",
          fontSize: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 10px",
          textAlign: "center",
          color: "#334155",
          fontWeight: 700,
        } as CSSProperties,
      });
      continue;
    }

    const step = node.step;
    if (!step) {
      continue;
    }

    const delayMinutes = Math.max(step.delay_minutes || 0, 0);

    nodes.push({
      id: nodeId,
      data: {
        label: (
          <Box
            position="relative"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            minW="100%"
          >
            <VStack spacing={0}>
              <Text fontSize="14px" fontWeight="600" color="#1F2937">
                {step.name || step.id}
              </Text>
              <Text fontSize="12px" fontWeight="500" color="#1F2937">
                In Step{": "}
                {automation.operational_metrics?.current_in_step?.[step.id] ||
                  0}
              </Text>
              {step.type === "EMAIL" ? <FaRegEnvelope /> : <TbArrowFork />}
            </VStack>
            {delayMinutes > 0 && (
              <Box
                position="absolute"
                fontSize="12px"
                fontWeight="500"
                top="-24px"
                left="50%"
                transform="translateX(-50%) translateY(-50%)"
                w="48px"
                h="48px"
                borderRadius="100%"
                bg="white"
                border={
                  selectedStepId === step.id
                    ? "2px solid #2563EB"
                    : "1px solid #D0D5DD"
                }
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                zIndex={2}
              >
                <Text lineHeight="1">⏱</Text>
                <Text>{delayedMinutesLabel(delayMinutes)}</Text>
              </Box>
            )}
          </Box>
        ),
      },
      position: pos,
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      style: {
        width: nodeWidth,
        minHeight: nodeHeight,
        borderRadius: 10,
        border:
          selectedStepId === step.id
            ? "2px solid #2563EB"
            : "1px solid #D0D5DD",
        background: selectedStepId === step.id ? "#EFF6FF" : "#FFFFFF",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px 10px",
        textAlign: "center",
        color: "#1F2937",
        fontWeight: 600,
        overflow: "visible",
      } as CSSProperties,
    });
  }

  const edges: Edge[] = [];
  const entryVisual = {
    color: "#667085",
    style: { stroke: "#667085", strokeWidth: 1.2, strokeDasharray: "6 4" },
  };
  edges.push({
    id: `${ENTRY_NODE_ID}-${startId}`,
    source: ENTRY_NODE_ID,
    target: startId,
    type: "step",
    style: entryVisual.style,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 10,
      height: 10,
      color: entryVisual.color,
    },
  });

  for (const edge of graphEdges) {
    const visual = getEdgeVisual(edge.kind);

    edges.push({
      id: `${edge.sourceId}-${edge.kind}-${edge.targetId}`,
      source: edge.sourceId,
      target: edge.targetId,
      type: "step",
      animated: visual.animated,
      label: visual.label,
      labelStyle: visual.labelStyle,
      labelBgStyle: visual.labelBgStyle,
      labelBgPadding: visual.labelBgPadding,
      style: visual.style,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 10,
        height: 10,
        color: visual.color,
      },
    });
  }

  return { nodes, edges };
};

export interface AutomationsFlowGraphProps {
  automation: KYDEngageAutomationDefinition;
  selectedStepId?: string;
  onSelectStep: (stepId: string) => void;
  height?: string;
}
const AutomationsFlowGraph = ({
  automation,
  selectedStepId,
  onSelectStep,
  height = "520px",
}: AutomationsFlowGraphProps) => {
  // recenter the graph after everything is loaded
  const [layoutEpoch, setLayoutEpoch] = useState(0);

  useEffect(() => {
    setLayoutEpoch(0);
    const timeoutId = window.setTimeout(() => {
      setLayoutEpoch((epoch) => epoch + 1);
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [automation.id]);

  const { nodes, edges } = useMemo(
    () => buildNodesAndEdges(automation, selectedStepId),
    [automation, selectedStepId, layoutEpoch]
  );

  if (automation.steps.length === 0) {
    return (
      <VStack alignItems="center" justifyContent="center" h="100%">
        <Text color="gray.500">No steps configured.</Text>
      </VStack>
    );
  }

  return (
    <Box
      h={height}
      w="100%"
      border="1px solid #E6E6E6"
      borderRadius="12px"
      overflow="hidden"
      bg="white"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={fitViewOptions}
        onNodeClick={(_, node) => {
          if (
            node.id === ENTRY_NODE_ID ||
            node.id.startsWith(EXIT_NODE_PREFIX)
          ) {
            return;
          }
          onSelectStep(node.id);
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false} fitViewOptions={fitViewOptions} />
        <Background gap={16} size={1} />
      </ReactFlow>
    </Box>
  );
};

export default AutomationsFlowGraph;
